import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Play, CheckCircle, XCircle, FileText, Info, Eye, EyeSlash, 
  Question, Code, CloudSlash, ChatCircle, Plus, X 
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import MentionInput from '../ui/MentionInput';
import JsonMentionTextarea from '../ui/JsonMentionTextarea';
import ApiHeadersInput from '../ApiHeadersInput';
import ApiQueryParamsInput from '../ApiQueryParamsInput';
import { API_BASE_URL } from '../../config/api';

const ApiActionTab = ({
  formData,
  settingId, // Add settingId prop for API testing
  validationErrors = {},
  showQuestions = false,
  setShowQuestions,
  selectedProviderId = null,
  userIntegrationsMap = {},
  availableProviders = [],
  onFormChange,
  onVariantChange,
  setHasUnsavedChanges,
  showBearerToken,
  setShowBearerToken,
  showBasicPassword,
  setShowBasicPassword,
  showApiKey,
  setShowApiKey,
  onIntegrationSelection
}) => {
  // Local state for this component
  const [isTestingAPI, setIsTestingAPI] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testInputs, setTestInputs] = useState({});
  const [showTestSection, setShowTestSection] = useState(false);
  const [showResponseTemplate, setShowResponseTemplate] = useState(false);

  // Format questions for mention components
  const questionsForMention = [
    // Add integration variables first if provider is selected
    ...(selectedProviderId ? [
      {
        id: 'integration_token',
        text: 'integration_token'
      },
      // Add credential variables from selected provider
      ...(Array.isArray(userIntegrationsMap[selectedProviderId]) ? userIntegrationsMap[selectedProviderId] : []).map(credentialType => ({
        id: credentialType,
        text: credentialType
      }))
    ] : []),
    // Add simple action question variables
    ...formData.questions?.map((q, index) => ({
      id: q.id,
      text: `question_${index + 1}_response`
    })) || []
  ];

  // API Testing functionality - simple and elegant
  const handleTestAPI = async () => {
    // Validate requirements first - Steve Jobs would want this to "just work"
    if (!formData.api_endpoint) {
      toast.error("Please enter an API endpoint first");
      return;
    }

    if (showQuestions && (!formData.questions || formData.questions.length === 0)) {
      toast.error("Please add at least one question to test the API");
      return;
    }

    setIsTestingAPI(true);
    setTestResult(null);

    try {
      // Get current user for the request
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) {
        toast.error("User authentication required for API testing");
        return;
      }

      // Initialize test inputs with sample data if empty
      const currentTestInputs = { ...testInputs };
      let hasEmptyInputs = false;

      if (showQuestions && formData.questions) {
        formData.questions.forEach((question, index) => {
          const questionKey = `question_${index + 1}_response`;
          if (!currentTestInputs[questionKey]) {
            hasEmptyInputs = true;
            // Provide intelligent defaults based on question type
            switch (question.type) {
              case 'boolean':
                currentTestInputs[questionKey] = 'yes';
                break;
              case 'numeric':
                currentTestInputs[questionKey] = '1';
                break;
              default:
                currentTestInputs[questionKey] = `Sample answer for ${question.question}`;
            }
          }
        });

        if (hasEmptyInputs) {
          setTestInputs(currentTestInputs);
        }
      }

      // Use the passed settingId prop or fallback for new actions
      const actionId = settingId || 'test_action_preview';

      if (!settingId) {
        toast.error("Cannot test API for unsaved actions. Please save the action first.");
        return;
      }

      // Make the API call through backend - backend fetches API config from database
      const response = await fetch(`${API_BASE_URL}/api/external/simple-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: accountId,
          simpleActionId: actionId,
          auth_type: formData.auth_type || 'none',
          auth_config: formData.auth_type === 'integration' && selectedProviderId 
            ? { ...formData.auth_config, api_provider_id: selectedProviderId }
            : formData.auth_config || {},
          ...currentTestInputs // Spread question responses directly at top level
        })
      });

      // Handle both JSON and non-JSON responses gracefully
      let responseData;
      const contentType = response.headers.get('content-type');

      try {
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
        } else {
          const textResponse = await response.text();
          responseData = { message: textResponse, raw: textResponse };
        }
      } catch (parseError) {
        const textResponse = await response.text();
        responseData = { message: textResponse, raw: textResponse };
      }

      if (!response.ok) {
        const errorMessage = responseData.message || responseData.error || responseData.raw || 'Unknown error';
        throw new Error(`API returned ${response.status}: ${errorMessage}`);
      }

      // Success - store the result
      setTestResult({
        success: true,
        data: responseData,
        status: response.status,
        timestamp: new Date().toISOString()
      });

      toast.success("API test successful! 🎉");

    } catch (error) {
      console.error('API test failed:', error);
      setTestResult({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      });
      toast.error(`API test failed: ${error.message}`);
    } finally {
      setIsTestingAPI(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Questions Toggle for API */}
      <div className="pt-3 sm:pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5 sm:space-y-1">
            <h4 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
              <Question className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 text-purple-500" />
              Ask Questions First
            </h4>
            <p className="text-[10px] sm:text-xs text-gray-500">
              Ask questions before making API call to personalize the request
            </p>
          </div>
          <Switch
            checked={showQuestions}
            onCheckedChange={(checked) => {
              setShowQuestions(checked);
              setHasUnsavedChanges(true);
              if (!checked) {
                onFormChange({ target: { name: 'questions', value: [] } });
              }
            }}
            className="data-[state=checked]:bg-purple-500"
          />
        </div>
      </div>

      {/* Questions Management Interface */}
      {showQuestions && (
        <div className="space-y-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-xs sm:text-sm font-medium text-gray-700">Questions</h3>
            <span className="text-[10px] sm:text-xs text-gray-500">
              {(formData.questions || []).length} question{(formData.questions || []).length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Add Question Form */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
            <div className="flex gap-2">
              <Input
                value={formData.currentQuestion || ''}
                onChange={(e) => onFormChange({ target: { name: 'currentQuestion', value: e.target.value } })}
                placeholder="Enter a question to ask"
                className="flex-1 h-8 text-sm"
              />
              <Select 
                value={formData.currentQuestionType || 'string'} 
                onValueChange={(value) => onFormChange({ target: { name: 'currentQuestionType', value } })}
              >
                <SelectTrigger className="w-24 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[10030]">
                  <SelectItem value="string">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="boolean">Yes/No</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                size="sm"
                className="h-8 px-3 text-xs"
                onClick={() => {
                  const currentQuestion = formData.currentQuestion?.trim();
                  if (!currentQuestion) return;

                  const newQuestion = {
                    id: Date.now().toString(),
                    question: currentQuestion,
                    type: formData.currentQuestionType || 'string',
                    order: (formData.questions || []).length
                  };

                  const updatedQuestions = [...(formData.questions || []), newQuestion];
                  onFormChange({ target: { name: 'questions', value: updatedQuestions } });
                  onFormChange({ target: { name: 'currentQuestion', value: '' } });
                  setHasUnsavedChanges(true);
                }}
                disabled={!formData.currentQuestion?.trim()}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add
              </Button>
            </div>
          </div>

          {/* Questions List */}
          {(formData.questions || []).length > 0 && (
            <div className="space-y-2">
              {(formData.questions || []).map((question, index) => (
                <div key={question.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-900">Q{index + 1}:</span>
                      <span className="text-xs text-gray-700">{question.question}</span>
                      <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                        {question.type === 'boolean' ? 'Yes/No' : question.type === 'number' ? 'Number' : 'Text'}
                      </span>
                    </div>
                    <div className="text-[10px] text-blue-600 mt-1">
                      Variable: question_{index + 1}_response
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const updatedQuestions = formData.questions.filter(q => q.id !== question.id);
                      onFormChange({ target: { name: 'questions', value: updatedQuestions } });
                      setHasUnsavedChanges(true);
                    }}
                    className="text-gray-400 hover:text-red-500 p-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Request Builder - Postman-like Interface */}
      <div className="bg-white rounded-xl border border-gray-200/50 shadow-[0_4px_20px_rgb(0,0,0,0.08)] overflow-hidden">
        {/* Request URL Bar */}
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            {/* HTTP Method Dropdown */}
            <div className="w-full sm:w-24">
              <Select 
                value={formData.api_method} 
                onValueChange={(value) => {
                  if (formData.showLocationVariants) {
                    onVariantChange('api_method', value);
                  } else {
                    onFormChange({ target: { name: 'api_method', value } });
                    setHasUnsavedChanges(true);
                  }
                }}
              >
                <SelectTrigger className="w-full h-10 sm:h-11 text-sm font-mono font-medium border-gray-200/50 bg-gray-50/50 focus:bg-white transition-all duration-200 rounded-lg shadow-inner">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[20000]">
                  <SelectItem value="GET" className="font-mono">
                    <span className="text-green-600 font-medium">GET</span>
                  </SelectItem>
                  <SelectItem value="POST" className="font-mono">
                    <span className="text-blue-600 font-medium">POST</span>
                  </SelectItem>
                  <SelectItem value="PUT" className="font-mono">
                    <span className="text-orange-600 font-medium">PUT</span>
                  </SelectItem>
                  <SelectItem value="PATCH" className="font-mono">
                    <span className="text-yellow-600 font-medium">PATCH</span>
                  </SelectItem>
                  <SelectItem value="DELETE" className="font-mono">
                    <span className="text-red-600 font-medium">DELETE</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* URL Input */}
            <div className="flex-1 relative">
              <MentionInput
                name="api_endpoint"
                value={formData.api_endpoint || ''}
                onChange={(e) => {
                  if (formData.showLocationVariants) {
                    onVariantChange(e.target.name, e.target.value);
                  } else {
                    onFormChange(e);
                  }
                }}
                questions={questionsForMention}
                placeholder="https://api.example.com/endpoint"
                className={cn(
                  "h-10 sm:h-11 text-xs sm:text-sm font-mono border-gray-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 rounded-lg shadow-inner pr-3",
                  validationErrors.api_endpoint && "border-red-300 focus:border-red-500 focus:ring-red-500"
                )}
              />
            </div>
          </div>
          {validationErrors.api_endpoint && (
            <motion.p 
              className="text-xs text-red-500 mt-2"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
            >
              {validationErrors.api_endpoint}
            </motion.p>
          )}
        </div>

        {/* Request Configuration Tabs */}
        <div className="px-4 sm:px-6">
          <Tabs defaultValue="params" className="w-full">
            <TabsList className="grid grid-cols-4 bg-gray-50/70 backdrop-blur-sm p-1 rounded-lg shadow-inner mb-4 sm:mb-6">
              <TabsTrigger value="params" className="rounded-md py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm">
                <span className="hidden sm:inline">Params</span>
                <span className="sm:hidden">?</span>
              </TabsTrigger>
              <TabsTrigger value="auth" className="rounded-md py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm">
                <span className="hidden sm:inline">Authorization</span>
                <span className="sm:hidden">🔑</span>
              </TabsTrigger>
              <TabsTrigger value="headers" className="rounded-md py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm">
                <span className="hidden sm:inline">Headers</span>
                <span className="sm:hidden">📋</span>
              </TabsTrigger>
              <TabsTrigger value="body" className="rounded-md py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm">
                <span className="hidden sm:inline">Body</span>
                <span className="sm:hidden">📝</span>
              </TabsTrigger>
            </TabsList>

            {/* Params Tab */}
            <TabsContent value="params" className="space-y-3 sm:space-y-4 pb-4 sm:pb-6">
              <div>
                <Label className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3 block">Query Parameters</Label>
                <ApiQueryParamsInput
                  queryParams={formData.api_query_params || {}}
                  questions={questionsForMention}
                  onChange={(queryParams) => {
                    if (formData.showLocationVariants) {
                      onVariantChange('api_query_params', queryParams);
                    } else {
                      onFormChange({ target: { name: 'api_query_params', value: queryParams } });
                      setHasUnsavedChanges(true);
                    }
                  }}
                />
              </div>

              <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-3 sm:p-4">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <Info className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-blue-900 mb-1">URL Parameters</h4>
                    <p className="text-[10px] sm:text-xs text-blue-700 leading-relaxed">
                      Query parameters will be automatically appended to your API endpoint URL.
                      Use @ mentions in parameter values to insert dynamic content.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Auth Tab */}
            <TabsContent value="auth" className="space-y-3 sm:space-y-4 pb-4 sm:pb-6">
              <div>
                <Label className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3 block">Authentication Type</Label>
                <Select 
                  value={formData.auth_type || 'none'} 
                  onValueChange={(value) => {
                    if (formData.showLocationVariants) {
                      onVariantChange('auth_type', value);
                      onVariantChange('auth_config', {});
                    } else {
                      onFormChange({ target: { name: 'auth_type', value } });
                      onFormChange({ target: { name: 'auth_config', value: {} } });
                      setHasUnsavedChanges(true);
                    }
                  }}
                >
                  <SelectTrigger className="w-full h-9 sm:h-10 text-xs sm:text-sm border-gray-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 rounded-lg shadow-inner">
                    <SelectValue placeholder="Select authentication type" />
                  </SelectTrigger>
                  <SelectContent className="z-[20000]">
                    <SelectItem value="none">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
                        <span className="text-xs sm:text-sm">No Auth</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="integration">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                        <span className="text-xs sm:text-sm">Use Integration</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="bearer">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                        <span className="text-xs sm:text-sm">Bearer Token</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="basic">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                        <span className="text-xs sm:text-sm">Basic Auth</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="api_key">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                        <span className="text-xs sm:text-sm">API Key</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Auth Config Fields */}
              {formData.auth_type === 'bearer' && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-3"
                >
                  <div>
                    <Label className="text-xs sm:text-sm font-medium text-gray-700 mb-2 block">Bearer Token</Label>
                    <div className="relative">
                      <Input
                        type={showBearerToken ? "text" : "password"}
                        value={formData.auth_config?.token || ''}
                        onChange={(e) => {
                          const newAuthConfig = { ...formData.auth_config, token: e.target.value };
                          if (formData.showLocationVariants) {
                            onVariantChange('auth_config', newAuthConfig);
                          } else {
                            onFormChange({ target: { name: 'auth_config', value: newAuthConfig } });
                            setHasUnsavedChanges(true);
                          }
                        }}
                        placeholder="Enter your bearer token"
                        className="h-9 sm:h-10 font-mono text-xs sm:text-sm border-purple-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 rounded-lg shadow-inner pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowBearerToken(!showBearerToken)}
                        className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showBearerToken ? (
                          <EyeSlash className="h-3 w-3 sm:h-4 sm:w-4" />
                        ) : (
                          <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {formData.auth_type === 'integration' && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-purple-50/50 border border-purple-200/50 rounded-lg p-3 sm:p-4 space-y-3 sm:space-y-4"
                >
                  <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <Label className="text-xs sm:text-sm font-medium text-purple-900">Integration Authentication</Label>
                  </div>

                  {availableProviders.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                      <p className="text-xs text-amber-700 mb-2">No connected integrations found</p>
                      <p className="text-[10px] text-amber-600">Connect an integration first to use this authentication method</p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label className="text-[10px] sm:text-xs text-purple-700 mb-1 block">Select Integration</Label>
                        <Select
                          value={selectedProviderId}
                          onValueChange={onIntegrationSelection}
                        >
                          <SelectTrigger className="h-9 sm:h-10 text-xs sm:text-sm border-purple-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 rounded-lg shadow-inner">
                            <SelectValue placeholder="Choose an integration" />
                          </SelectTrigger>
                          <SelectContent className="z-[20001]">
                            {availableProviders.map(provider => (
                              <SelectItem key={provider.id} value={provider.id}>
                                <div className="flex items-center">
                                  <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                                  <span>{provider.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {selectedProviderId && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="bg-white/50 border border-purple-200/50 rounded-lg p-3 space-y-2"
                        >
                          <Label className="text-[10px] sm:text-xs text-purple-700 font-medium">Available Template Variables</Label>

                          <div className="space-y-1">
                            <div className="text-[10px] text-purple-600 font-mono bg-purple-100/50 px-2 py-1 rounded">
                              {`{{integration_token}}`} - Auth token
                            </div>
                                                          {(Array.isArray(userIntegrationsMap[selectedProviderId]) ? userIntegrationsMap[selectedProviderId] : []).map(credentialType => (
                                <div key={credentialType} className="text-[10px] text-purple-600 font-mono bg-purple-100/50 px-2 py-1 rounded">
                                  {`{{${credentialType}}}`} - Available credential
                                </div>
                              ))}
                          </div>

                          <div className="bg-blue-50/50 border border-blue-200/50 rounded p-2 mt-2">
                            <div className="flex items-start space-x-2">
                              <Info className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                              <p className="text-[9px] sm:text-[10px] text-blue-700">
                                Use these variables in your API endpoint, headers, or body. They'll be automatically replaced with real values when the API is called.
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </>
                  )}
                </motion.div>
              )}

              {formData.auth_type === 'basic' && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3"
                >
                  <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <Label className="text-xs sm:text-sm font-medium text-blue-900">Basic Authentication</Label>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] sm:text-xs text-blue-700 mb-1 block">Username</Label>
                      <Input
                        value={formData.auth_config?.username || ''}
                        onChange={(e) => {
                          const newAuthConfig = { ...formData.auth_config, username: e.target.value };
                          if (formData.showLocationVariants) {
                            onVariantChange('auth_config', newAuthConfig);
                          } else {
                            onFormChange({ target: { name: 'auth_config', value: newAuthConfig } });
                            setHasUnsavedChanges(true);
                          }
                        }}
                        placeholder="Username"
                        className="h-9 sm:h-10 text-xs sm:text-sm border-blue-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 rounded-lg shadow-inner"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] sm:text-xs text-blue-700 mb-1 block">Password</Label>
                      <div className="relative">
                        <Input
                          type={showBasicPassword ? "text" : "password"}
                          value={formData.auth_config?.password || ''}
                          onChange={(e) => {
                            const newAuthConfig = { ...formData.auth_config, password: e.target.value };
                            if (formData.showLocationVariants) {
                              onVariantChange('auth_config', newAuthConfig);
                            } else {
                              onFormChange({ target: { name: 'auth_config', value: newAuthConfig } });
                              setHasUnsavedChanges(true);
                            }
                          }}
                          placeholder="Password"
                          className="h-9 sm:h-10 text-xs sm:text-sm border-blue-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 rounded-lg shadow-inner pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowBasicPassword(!showBasicPassword)}
                          className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {showBasicPassword ? (
                            <EyeSlash className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {formData.auth_type === 'api_key' && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-purple-50/50 border border-purple-200/50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3"
                >
                  <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <Label className="text-xs sm:text-sm font-medium text-purple-900">API Key</Label>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] sm:text-xs text-purple-700 mb-1 block">Header Name</Label>
                      <Input
                        value={formData.auth_config?.key_name || ''}
                        onChange={(e) => {
                          const newAuthConfig = { ...formData.auth_config, key_name: e.target.value };
                          if (formData.showLocationVariants) {
                            onVariantChange('auth_config', newAuthConfig);
                          } else {
                            onFormChange({ target: { name: 'auth_config', value: newAuthConfig } });
                            setHasUnsavedChanges(true);
                          }
                        }}
                        placeholder="X-API-Key"
                        className="h-9 sm:h-10 font-mono text-xs sm:text-sm border-purple-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 rounded-lg shadow-inner"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] sm:text-xs text-purple-700 mb-1 block">API Key Value</Label>
                      <div className="relative">
                        <Input
                          type={showApiKey ? "text" : "password"}
                          value={formData.auth_config?.key_value || ''}
                          onChange={(e) => {
                            const newAuthConfig = { ...formData.auth_config, key_value: e.target.value };
                            if (formData.showLocationVariants) {
                              onVariantChange('auth_config', newAuthConfig);
                            } else {
                              onFormChange({ target: { name: 'auth_config', value: newAuthConfig } });
                              setHasUnsavedChanges(true);
                            }
                          }}
                          placeholder="Enter API key"
                          className="h-9 sm:h-10 font-mono text-xs sm:text-sm border-purple-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 rounded-lg shadow-inner pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {showApiKey ? (
                            <EyeSlash className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {formData.auth_type === 'none' && (
                <div className="bg-gray-50/50 border border-gray-200/50 rounded-lg p-3 sm:p-4 text-center">
                  <div className="text-gray-500 text-xs sm:text-sm">No authentication required for this request</div>
                </div>
              )}
            </TabsContent>

            {/* Headers Tab */}
            <TabsContent value="headers" className="space-y-3 sm:space-y-4 pb-4 sm:pb-6">
              <div>
                <Label className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3 block">Custom Headers</Label>
                <ApiHeadersInput 
                  headers={formData.api_headers || {}}
                  onChange={(headers) => {
                    if (formData.showLocationVariants) {
                      onVariantChange('api_headers', headers);
                    } else {
                      onFormChange({ target: { name: 'api_headers', value: headers } });
                      setHasUnsavedChanges(true);
                    }
                  }}
                />
              </div>
            </TabsContent>

            {/* Body Tab */}
            <TabsContent value="body" className="space-y-3 sm:space-y-4 pb-4 sm:pb-6">
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <Label className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3 block">Request Body (JSON)</Label>
                  <JsonMentionTextarea
                    name="api_body"
                    value={formData.api_body || ''}
                    onChange={(e) => {
                      if (formData.showLocationVariants) {
                        onVariantChange(e.target.name, e.target.value);
                      } else {
                        onFormChange(e);
                      }
                    }}
                    questions={questionsForMention}
                    placeholder='{\n  "key": "value",\n  "data": "example"\n}'
                    className={cn(
                      "min-h-[150px] sm:min-h-[200px] text-xs sm:text-sm border-gray-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 resize-none rounded-lg shadow-inner font-mono",
                      validationErrors.api_body && "border-red-300 focus:border-red-500 focus:ring-red-500"
                    )}
                  />
                  {validationErrors.api_body && (
                    <motion.p 
                      className="text-xs text-red-500 mt-2"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                    >
                      {validationErrors.api_body}
                    </motion.p>
                  )}
                </div>

                <div className="bg-amber-50/50 border border-amber-200/50 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <Info className="h-3 w-3 sm:h-4 sm:w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium text-amber-900 mb-1">JSON Body Template</h4>
                      <p className="text-[10px] sm:text-xs text-amber-700 leading-relaxed">
                        Type @ to insert dynamic variables. The placeholders will be replaced with actual values when the API is called.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Response Template Section - Simple and elegant */}
      <div className="bg-white rounded-xl border border-gray-200/50 shadow-[0_4px_20px_rgb(0,0,0,0.08)] overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5 sm:space-y-1">
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-purple-500" />
                Response Template
              </h4>
              <p className="text-xs text-gray-500">
                Customize how the AI responds using the API response data
              </p>
            </div>
            <Switch
              checked={showResponseTemplate}
              onCheckedChange={setShowResponseTemplate}
              className="data-[state=checked]:bg-purple-500"
            />
          </div>
        </div>

        {showResponseTemplate && (
          <div className="p-4 sm:p-6 space-y-4">
            <JsonMentionTextarea
              name="api_response_template"
              value={formData.api_response_template || ''}
              onChange={(e) => {
                if (formData.showLocationVariants) {
                  onVariantChange(e.target.name, e.target.value);
                } else {
                  onFormChange(e);
                }
              }}
              questions={questionsForMention}
              placeholder="Based on the API response: {{response_data}}, I can help you with..."
              className="min-h-[100px] text-sm border-gray-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 resize-none rounded-lg shadow-inner"
            />

            <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-1">Response Variables</h4>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    Use {"{{response_data}}"} to reference API response data, or @ to insert question responses and integration variables.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* API Testing Section - Steve Jobs would love this simplicity */}
      <div className="bg-white rounded-xl border border-gray-200/50 shadow-[0_4px_20px_rgb(0,0,0,0.08)] overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5 sm:space-y-1">
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <Play className="h-4 w-4 mr-2 text-green-500" />
                Test API
              </h4>
              <p className="text-xs text-gray-500">
                Test your API configuration with sample data
              </p>
            </div>
            <Switch
              checked={showTestSection}
              onCheckedChange={setShowTestSection}
              className="data-[state=checked]:bg-green-500"
            />
          </div>
        </div>

        {showTestSection && (
          <div className="p-4 sm:p-6 space-y-4">
            {/* Sample Inputs */}
            {showQuestions && formData.questions && formData.questions.length > 0 && (
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-700">Sample Inputs</h5>
                {formData.questions.map((question, index) => {
                  const questionKey = `question_${index + 1}_response`;
                  return (
                    <div key={question.id} className="space-y-1">
                      <label className="text-xs text-gray-600">{question.question}</label>
                      <Input
                        value={testInputs[questionKey] || ''}
                        onChange={(e) => setTestInputs(prev => ({ ...prev, [questionKey]: e.target.value }))}
                        placeholder={`Sample answer for: ${question.question}`}
                        className="text-sm border-gray-200 bg-gray-50 focus:bg-white"
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Test Button */}
            <div className="flex items-center space-x-3">
              <Button
                type="button"
                onClick={handleTestAPI}
                disabled={isTestingAPI || !formData.api_endpoint}
                className={cn(
                  "flex items-center space-x-2 font-medium transition-all duration-200",
                  isTestingAPI 
                    ? "bg-gray-400 cursor-not-allowed" 
                    : "bg-green-600 hover:bg-green-700 text-white"
                )}
              >
                {isTestingAPI ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Testing...</span>
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    <span>Send Test Request</span>
                  </>
                )}
              </Button>

              {testResult && (
                <div className={cn(
                  "flex items-center space-x-2 text-sm font-medium",
                  testResult.success ? "text-green-600" : "text-red-600"
                )}>
                  {testResult.success ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Success ({testResult.status})</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" />
                      <span>Failed</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Test Results */}
            {testResult && (
              <div className="mt-4 space-y-3">
                <h5 className="text-sm font-medium text-gray-700">Response</h5>
                <div className={cn(
                  "p-4 rounded-lg border text-sm font-mono whitespace-pre-wrap max-h-64 overflow-y-auto",
                  testResult.success 
                    ? "bg-green-50 border-green-200 text-green-800" 
                    : "bg-red-50 border-red-200 text-red-800"
                )}>
                  {testResult.success 
                    ? JSON.stringify(testResult.data, null, 2)
                    : testResult.error
                  }
                </div>
                <p className="text-xs text-gray-500">
                  Tested at {new Date(testResult.timestamp).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiActionTab; 